import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { List } from "../types.js";
import type { ConfigurationApi } from "./shared-api/shared-api-context.js";
import { useSharedApi } from "./shared-api/use-shared-api.js";

export const getWorkflowListsQueryKey = (configurationApi: ConfigurationApi, workflowId: number) =>
  configurationApi.queryOptions("get", "/api/workflow/{workflowId}/List", {
    params: { path: { workflowId } },
  }).queryKey;

export const fetchWorkflowLists = async (
  configurationApi: ConfigurationApi,
  workflowId: number
): Promise<List[]> => {
  const response = await configurationApi.client.GET("/api/workflow/{workflowId}/List", {
    params: { path: { workflowId } },
  });

  if (response.error) {
    throw new Error(`Failed to fetch lists: ${response.error}`);
  }

  return (response.data || []) as List[];
};

export const useWorkflowLists = (workflowId: number, enabled = true) => {
  const { configurationApi } = useSharedApi();
  const { data, isLoading, error, refetch } = useQuery<List[]>({
    queryKey: getWorkflowListsQueryKey(configurationApi, workflowId),
    queryFn: () => fetchWorkflowLists(configurationApi, workflowId),
    meta: { errorTitle: "Failed to load lists" },
    enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    lists: data || [],
    isLoading,
    error,
    refetch,
  };
};

export const useMultipleWorkflowLists = (workflowIds: number[]) => {
  const { configurationApi } = useSharedApi();
  const queries = useQueries({
    queries: workflowIds.map((workflowId) => ({
      queryKey: getWorkflowListsQueryKey(configurationApi, workflowId),
      queryFn: () => fetchWorkflowLists(configurationApi, workflowId),
      staleTime: 10 * 60 * 1000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);

  const lists = useMemo(() => {
    const allLists: List[] = [];
    const seenIds = new Set<number>();
    queries.forEach((q) => {
      if (q.data) {
        q.data.forEach((list) => {
          if (!seenIds.has(list.id)) {
            seenIds.add(list.id);
            allLists.push(list);
          }
        });
      }
    });
    return allLists;
  }, [queries]);

  return { lists, isLoading };
};

export const usePrefetchWorkflowLists = () => {
  const { configurationApi } = useSharedApi();
  const queryClient = useQueryClient();

  const prefetchLists = async (workflowId: number) => {
    await queryClient.prefetchQuery({
      queryKey: getWorkflowListsQueryKey(configurationApi, workflowId),
      queryFn: () => fetchWorkflowLists(configurationApi, workflowId),
      staleTime: 10 * 60 * 1000,
    });
  };

  return { prefetchLists };
};
